<template>
    <div>
        <!-- Bank name -->
        <div class="row tw-mb-4">
            <div class="col-md-6">
                <label class="required form-col-label control-label">{{
                    $t("bankProfile.bankName")
                }}</label>
                <Input
                    v-model="model.bank_name"
                    :placeholder="$t('bankProfile.bankName')"
                    :class="{ 'is-invalid': validate.has('bank_name') }"
                />
                <div class="invalid-feedback" v-if="validate.has('bank_name')">
                    {{ validate.first("bank_name") }}
                </div>
            </div>
        </div>

        <!-- Accounts -->
        <div v-if="showAccounts">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <h6 class="tw-font-semibold tw-mb-0">{{ $t("bankProfile.accounts") }}</h6>
                <Button v-if="!readonly" size="small" type="primary" ghost @click="addRow">
                    <i class="fas fa-plus tw-mr-1"></i>{{ $t("addNew") }}
                </Button>
            </div>

            <!-- Readonly: plain table -->
            <Table
                v-if="readonly"
                :columns="readonlyColumns"
                :data="model.accounts || []"
                size="small"
                stripe
                :no-data-text="$t('bankProfile.noAccounts')"
            >
                <template slot-scope="{ row }" slot="qr_readonly">
                    <img
                        v-if="row.qr_image"
                        :src="row.qr_image"
                        class="tw-w-8 tw-h-8 tw-object-cover tw-rounded tw-border tw-border-gray-200 tw-mx-auto"
                    />
                    <span v-else class="tw-text-gray-300">—</span>
                </template>
            </Table>

            <!-- Editable: inline inputs -->
            <Table
                v-else
                :columns="editableColumns"
                :data="model.accounts"
                size="small"
                stripe
                :no-data-text="$t('bankProfile.noAccounts')"
            >
                <template slot-scope="{ index }" slot="employee_id">
                    <Select
                        v-model="model.accounts[index].employee_id"
                        filterable
                        remote
                        clearable
                        size="small"
                        transfer
                        :remote-method="q => searchEmployee(q, index)"
                        :loading="loadingEmployee[index]"
                        :placeholder="$t('bankProfile.searchEmployee')"
                        @on-open-change="opened => opened && searchEmployee('', index)"
                    >
                        <Option
                            v-for="emp in employeeOptions[index] || []"
                            :key="emp.employee_id"
                            :value="emp.employee_id"
                            :label="emp.employee_name_en || emp.employee_name_kh"
                        >
                            <span class="tw-font-mono tw-text-gray-400 tw-text-xs tw-mr-1">{{ emp.card_id }}</span>
                            <span class="tw-text-gray-800">{{ emp.employee_name_en }}</span>
                            <span v-if="emp.employee_name_kh" class="tw-text-gray-400 tw-text-xs tw-ml-1">{{ emp.employee_name_kh }}</span>
                        </Option>
                    </Select>
                </template>

                <template slot-scope="{ index }" slot="account_no">
                    <Input
                        v-model="model.accounts[index].account_no"
                        size="small"
                        :placeholder="$t('bankProfile.accountNo')"
                        :class="{ 'is-invalid': validate.has(`accounts.${index}.account_no`) }"
                    />
                    <div
                        class="invalid-feedback tw-text-xs"
                        v-if="validate.has(`accounts.${index}.account_no`)"
                    >
                        {{ validate.first(`accounts.${index}.account_no`) }}
                    </div>
                </template>

                <template slot-scope="{ index }" slot="account_name">
                    <Input
                        v-model="model.accounts[index].account_name"
                        size="small"
                        :placeholder="$t('bankProfile.accountName')"
                        :class="{ 'is-invalid': validate.has(`accounts.${index}.account_name`) }"
                    />
                    <div
                        class="invalid-feedback tw-text-xs"
                        v-if="validate.has(`accounts.${index}.account_name`)"
                    >
                        {{ validate.first(`accounts.${index}.account_name`) }}
                    </div>
                </template>

                <template slot-scope="{ index }" slot="qr_image">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <img
                            v-if="model.accounts[index].qr_image"
                            :src="model.accounts[index].qr_image"
                            class="tw-w-8 tw-h-8 tw-object-cover tw-rounded tw-border tw-border-gray-200 tw-flex-shrink-0"
                        />
                        <label
                            class="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-cursor-pointer tw-flex-shrink-0"
                            :class="uploadingQr[index] ? 'tw-bg-gray-100' : 'tw-bg-blue-50 hover:tw-bg-blue-100'"
                            :title="$t('bankProfile.uploadQr')"
                        >
                            <i v-if="uploadingQr[index]" class="fas fa-circle-notch fa-spin tw-text-gray-400" style="font-size:12px;"></i>
                            <i v-else class="fas fa-qrcode tw-text-blue-500" style="font-size:13px;"></i>
                            <input
                                type="file"
                                accept="image/*"
                                class="tw-hidden"
                                :disabled="!!uploadingQr[index]"
                                @change="onQrChange($event, index)"
                            />
                        </label>
                        <a
                            v-if="model.accounts[index].qr_image"
                            href="#"
                            class="tw-text-gray-300 hover:tw-text-red-400"
                            @click.prevent="model.accounts[index].qr_image = null"
                            :title="$t('delete')"
                        >
                            <i class="fas fa-times" style="font-size:11px;"></i>
                        </a>
                    </div>
                </template>

                <template slot-scope="{ index }" slot="is_receive_cash_transfer">
                    <i-switch
                        v-model="model.accounts[index].is_receive_cash_transfer"
                        size="small"
                    />
                </template>

                <template slot-scope="{ index }" slot="action">
                    <a
                        href="#"
                        class="text-danger"
                        @click.prevent="removeRow(index)"
                        v-tooltip="$t('delete')"
                    >
                        <Icon type="ios-trash" size="18" />
                    </a>
                </template>
            </Table>
        </div>
    </div>
</template>

<script>
import { debounce } from "lodash";

export default {
    name: "BankProfileFormInput",
    data() {
        return {
            uploadingQr: {},
            employeeOptions: {},
            loadingEmployee: {}
        };
    },
    props: {
        value: { type: Object, required: true },
        validate: { type: Object, required: true },
        showAccounts: { type: Boolean, default: true },
        readonly: { type: Boolean, default: false }
    },
    watch: {
        'value.accounts': {
            immediate: true,
            handler(accounts) {
                if (!accounts) return;
                accounts.forEach((a, i) => {
                    if (a.employee && !this.employeeOptions[i]) {
                        this.$set(this.employeeOptions, i, [a.employee]);
                    }
                });
            }
        }
    },
    computed: {
        model() {
            return this.value;
        },
        readonlyColumns() {
            return [
                {
                    title: this.$t("bankProfile.employeeId"),
                    key: "employee_id",
                    width: 200,
                    align: "center"
                },
                {
                    title: this.$t("bankProfile.accountNo"),
                    key: "account_no",
                    minWidth: 160
                },
                {
                    title: this.$t("bankProfile.accountName"),
                    key: "account_name",
                    minWidth: 200
                },
                {
                    title: this.$t("bankProfile.qrImage"),
                    slot: "qr_readonly",
                    width: 70,
                    align: "center"
                },
                {
                    title: this.$t("bankProfile.isReceiveCashTransfer"),
                    key: "is_receive_cash_transfer",
                    width: 140,
                    align: "center",
                    render: (h, { row }) => h("i-switch", { props: { value: row.is_receive_cash_transfer, size: "small", disabled: true } })
                }
            ];
        },
        editableColumns() {
            return [
                {
                    title: this.$t("bankProfile.employeeId"),
                    slot: "employee_id",
                    width: 200
                },
                {
                    title: this.$t("bankProfile.accountNo"),
                    slot: "account_no",
                    minWidth: 160
                },
                {
                    title: this.$t("bankProfile.accountName"),
                    slot: "account_name",
                    minWidth: 200
                },
                {
                    title: this.$t("bankProfile.qrImage"),
                    slot: "qr_image",
                    width: 110,
                    align: "center"
                },
                {
                    title: this.$t("bankProfile.isReceiveCashTransfer"),
                    slot: "is_receive_cash_transfer",
                    width: 140,
                    align: "center"
                },
                {
                    title: "",
                    slot: "action",
                    align: "center",
                    width: 60
                }
            ];
        }
    },
    methods: {
        addRow() {
            this.model.accounts.push({ employee_id: null, account_no: "", account_name: "", qr_image: null, is_receive_cash_transfer: false });
        },
        removeRow(index) {
            this.model.accounts.splice(index, 1);
        },
        onQrChange(event, index) {
            const file = event.target.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append("photo", file);
            this.$set(this.uploadingQr, index, true);
            this.$store
                .dispatch("finance/bankProfile/qrUpload", formData)
                .then(path => { this.model.accounts[index].qr_image = `/api/files?filePath=${path}`; })
                .finally(() => { this.$set(this.uploadingQr, index, false); });
            event.target.value = "";
        },
        searchEmployee: debounce(function(query, index) {
            this.$set(this.loadingEmployee, index, true);
            this.$store
                .dispatch("finance/bankProfile/searchEmployees", query)
                .then(data => { this.$set(this.employeeOptions, index, data); })
                .finally(() => { this.$set(this.loadingEmployee, index, false); });
        }, 400)
    }
};
</script>
